export const idlFactory = ({ IDL }) => {
  const ServiceId = IDL.Text;
  const DeviceId = IDL.Text;
  const ServiceTier = IDL.Variant({
    'Entry' : IDL.Null,
    'Application' : IDL.Null,
    'Storage' : IDL.Null,
    'Network' : IDL.Null,
    'Database' : IDL.Null,
    'LoadBalancer' : IDL.Null,
  });
  const HealthStatus = IDL.Variant({
    'Healthy' : IDL.Null,
    'Critical' : IDL.Null,
    'Offline' : IDL.Null,
    'Unknown' : IDL.Null,
    'Warning' : IDL.Null,
  });
  const ServiceDevice = IDL.Record({
    'os' : IDL.Text,
    'public_ip' : IDL.Text,
    'name' : IDL.Text,
    'tier' : ServiceTier,
    'hostname' : IDL.Text,
    'device_id' : DeviceId,
    'accessible' : IDL.Bool,
    'health_status' : HealthStatus,
    'private_ip' : IDL.Text,
    'discovered_at' : IDL.Nat64,
    'ports' : IDL.Vec(IDL.Tuple(IDL.Nat16, IDL.Text)),
    'last_check' : IDL.Nat64,
  });
  const DiscoveryStatus = IDL.Variant({
    'Failed' : IDL.Null,
    'Running' : IDL.Null,
    'Completed' : IDL.Null,
  });
  const DiscoverySession = IDL.Record({
    'id' : IDL.Text,
    'status' : DiscoveryStatus,
    'errors' : IDL.Vec(IDL.Text),
    'service_id' : ServiceId,
    'connections_found' : IDL.Nat32,
    'completed_at' : IDL.Opt(IDL.Nat64),
    'devices_discovered' : IDL.Nat32,
    'started_at' : IDL.Nat64,
  });
  const ServiceConnection = IDL.Record({
    'to_device' : DeviceId,
    'validated' : IDL.Bool,
    'port' : IDL.Nat16,
    'connection_type' : IDL.Text,
    'from_device' : DeviceId,
    'discovered_at' : IDL.Nat64,
  });
  const BusinessService = IDL.Record({
    'sla_target' : IDL.Text,
    'updated_at' : IDL.Nat64,
    'annual_revenue' : IDL.Text,
    'bus_service_id' : ServiceId,
    'name' : IDL.Text,
    'description' : IDL.Text,
    'created_at' : IDL.Nat64,
    'connections' : IDL.Vec(ServiceConnection),
    'customer_count' : IDL.Text,
    'health_score' : IDL.Float32,
    'tier_breakdown' : IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat32)),
    'devices' : IDL.Vec(DeviceId),
    'entry_point' : IDL.Text,
  });
  const ServiceMapDevice = IDL.Record({
    'config_files' : IDL.Vec(IDL.Text),
    'role' : IDL.Text,
    'hostname' : IDL.Text,
    'device_id' : IDL.Text,
    'ip_address' : IDL.Text,
  });
  const ServiceMapTier = IDL.Record({
    'tier_name' : IDL.Text,
    'tier_level' : IDL.Nat32,
    'devices' : IDL.Vec(ServiceMapDevice),
  });
  const ServiceMapConnection = IDL.Record({
    'to_device' : IDL.Text,
    'port' : IDL.Opt(IDL.Nat32),
    'connection_type' : IDL.Text,
    'from_device' : IDL.Text,
  });
  const ServiceMap = IDL.Record({
    'tiers' : IDL.Vec(ServiceMapTier),
    'discovery_logs' : IDL.Vec(IDL.Text),
    'service_name' : IDL.Text,
    'total_devices' : IDL.Nat32,
    'connections' : IDL.Vec(ServiceMapConnection),
    'discovered_at' : IDL.Nat64,
  });
  return IDL.Service({
    'add_connection' : IDL.Func(
        [ServiceId, DeviceId, DeviceId, IDL.Text, IDL.Nat16],
        [IDL.Variant({ 'Ok' : IDL.Bool, 'Err' : IDL.Text })],
        [],
      ),
    'add_device_to_service' : IDL.Func(
        [
          ServiceId,
          DeviceId,
          IDL.Text,
          IDL.Text,
          IDL.Text,
          IDL.Text,
          ServiceTier,
          IDL.Text,
          IDL.Bool,
          IDL.Vec(IDL.Tuple(IDL.Nat16, IDL.Text)),
        ],
        [IDL.Variant({ 'Ok' : IDL.Bool, 'Err' : IDL.Text })],
        [],
      ),
    'count_service_maps' : IDL.Func([], [IDL.Nat32], ['query']),
    'create_service' : IDL.Func(
        [IDL.Text, IDL.Text, IDL.Text, IDL.Text, IDL.Text, IDL.Text],
        [IDL.Variant({ 'Ok' : ServiceId, 'Err' : IDL.Text })],
        [],
      ),
    'delete_service_map' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : IDL.Null, 'Err' : IDL.Text })],
        [],
      ),
    'get_device' : IDL.Func([DeviceId], [IDL.Opt(ServiceDevice)], ['query']),
    'get_discovery_session' : IDL.Func(
        [IDL.Text],
        [IDL.Opt(DiscoverySession)],
        ['query'],
      ),
    'get_service' : IDL.Func(
        [ServiceId],
        [IDL.Opt(BusinessService)],
        ['query'],
      ),
    'get_service_map' : IDL.Func([IDL.Text], [IDL.Opt(ServiceMap)], ['query']),
    'get_service_map_logs' : IDL.Func(
        [IDL.Text],
        [IDL.Opt(IDL.Vec(IDL.Text))],
        ['query'],
      ),
    'get_service_map_versions' : IDL.Func(
        [IDL.Text, IDL.Opt(IDL.Nat32)],
        [IDL.Vec(ServiceMap)],
        ['query'],
      ),
    'get_service_topology' : IDL.Func(
        [ServiceId],
        [
          IDL.Variant({
            'Ok' : IDL.Tuple(
              BusinessService,
              IDL.Vec(ServiceDevice),
              IDL.Vec(ServiceConnection),
            ),
            'Err' : IDL.Text,
          }),
        ],
        ['query'],
      ),
    'get_stats' : IDL.Func(
        [],
        [IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat32))],
        ['query'],
      ),
    'list_service_maps' : IDL.Func([], [IDL.Vec(ServiceMap)], ['query']),
    'list_services' : IDL.Func([], [IDL.Vec(BusinessService)], ['query']),
    'start_discovery_session' : IDL.Func(
        [ServiceId],
        [IDL.Variant({ 'Ok' : IDL.Text, 'Err' : IDL.Text })],
        [],
      ),
    'store_service_map' : IDL.Func(
        [ServiceMap],
        [IDL.Variant({ 'Ok' : IDL.Null, 'Err' : IDL.Text })],
        [],
      ),
    'update_discovery_session' : IDL.Func(
        [IDL.Text, DiscoveryStatus, IDL.Nat32, IDL.Nat32, IDL.Vec(IDL.Text)],
        [IDL.Variant({ 'Ok' : IDL.Bool, 'Err' : IDL.Text })],
        [],
      ),
  });
};
export const init = ({ IDL }) => { return []; };
